import { useState } from 'react';
import { deleteResource } from '../../../apiServices/resourceApi';
import { deleteFolder } from '../../../apiServices/folderApi';
import { deleteWorkspace } from '../../../apiServices/groupApi';
import { Button } from '../../ui/Button';
import { Spinner } from '../../ui/Spinner';
import { useNotification } from '@/hooks/useNotification';

interface DeleteItemModalProps {
  selectedItems?: Array<{ id: string; itemtype?: string; resources?: Record<string, number>; [k: string]: unknown }>;
  action?: () => Promise<void>;
  closeModal: () => void;
  refreshList?: (force?: boolean, unused?: undefined, hard?: boolean) => void;
  updateIsQuerying: (querying: boolean) => void;
  userIsAdmin?: boolean;
}

export default function DeleteItemModal({
  selectedItems = [],
  action,
  closeModal,
  refreshList,
  updateIsQuerying,
  userIsAdmin,
}: DeleteItemModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { notify } = useNotification();

  const deleteResourceWrapper = async (item: DeleteItemModalProps['selectedItems'][number]) => {
    try {
      updateIsQuerying(true);
      await deleteResource(item.id);
      updateIsQuerying(false);
      notify({ type: 'success', message: 'File deleted' });
    } catch {
      notify({ type: 'error', message: 'Could not delete file' });
      updateIsQuerying(false);
    }
  };

  const getFolderHasContent = (item: DeleteItemModalProps['selectedItems'][number]) => {
    if (!item.resources) return false;
    return Object.values(item.resources).some((value) => value > 0);
  };

  const deleteItem = async (item: DeleteItemModalProps['selectedItems'][number]) => {
    if (getFolderHasContent(item)) {
      notify({ type: 'info', message: "Can't delete a folder with contents (delete items inside first)" });
      return;
    }
    try {
      updateIsQuerying(true);
      const response = await deleteFolder(item.id);
      updateIsQuerying(false);
      if ((response as { response: string }).response.search('ERROR') === -1) {
        notify({ type: 'success', message: 'Item deleted' });
      } else {
        notify({ type: 'error', message: 'Could not delete item' });
      }
    } catch {
      updateIsQuerying(false);
      notify({ type: 'error', message: 'Could not delete item' });
    }
  };

  const handleWorkspaceDelete = async (item: DeleteItemModalProps['selectedItems'][number]) => {
    if (!userIsAdmin) {
      notify({ type: 'info', message: 'Only admins can delete workspaces' });
      return;
    }
    try {
      updateIsQuerying(true);
      const response = await deleteWorkspace(item.id);
      updateIsQuerying(false);
      const resStr = (response as { response: string }).response;
      if (resStr.search('ERROR') === -1) {
        notify({ type: 'success', message: 'Workspace deleted' });
      } else if (resStr.search('group not empty') !== -1) {
        notify({ type: 'info', message: "Can't delete workspace with contents (delete items inside first)" });
      } else {
        notify({ type: 'error', message: 'Could not delete workspace' });
      }
    } catch {
      updateIsQuerying(false);
      notify({ type: 'error', message: 'Could not delete workspace' });
    }
  };

  const handleDelete = async () => {
    setIsLoading(true);
    if (!action) {
      await Promise.all(
        selectedItems.map(async (item) => {
          const itemtype = item.itemtype;
          if (itemtype === 'resource') {
            await deleteResourceWrapper(item);
          } else if (itemtype === 'folder' || itemtype === 'project') {
            await deleteItem(item);
          } else {
            await handleWorkspaceDelete(item);
          }
        }),
      );
      setIsLoading(false);
      refreshList?.(false, undefined, true);
      closeModal();
    } else {
      try {
        await action();
        setIsLoading(false);
        refreshList?.(false, undefined, true);
        closeModal();
      } catch (err) {
        console.error(err);
        setIsLoading(false);
      }
    }
  };

  return (
    <div>
      <Button variant="destructive" onClick={handleDelete} disabled={isLoading}>
        {isLoading && <Spinner size="sm" />}
        Delete
      </Button>
    </div>
  );
}
